import { PATIENT_CAMERA } from './patient-camera'
import type { ShenAiMeasurementResults, ShenAiVitalClient, ShenAiVitalClientOptions } from './shenai-types'

/** Where the vendor bundle is served from. Static assets, untouched by Vite's bundler. */
const SDK_MODULE_URL = '/sdk/shenai/index.mjs'
const SDK_DIRECTORY = '/sdk/shenai'

/** Whether this build carries the camera-replacement shim (vite.config.ts says why at length). */
declare const __FH_CAMERA_REPLACEMENT__: boolean

declare global {
  interface Window {
    __shenaiCreateSdk?: (args: unknown) => Promise<VendorSdk>
  }
}

/** The vendor's own SDK object, as `CreateShenaiSDK` resolves it. Only what this app calls. */
interface VendorSdk {
  initialize(
    apiKey: string,
    userId: string,
    settings: Record<string, unknown>,
    onResult: (result: { value: number }) => void,
  ): void
  isInitialized(): boolean
  deinitialize(): void
  destroyRuntime?: () => void
  attachToCanvas(canvasId: string, exclusive?: boolean): void
  setOperatingMode(mode: { value: number }): void
  getOperatingMode(): { value: number }
  setMediaStream(stream: MediaStream, facingUser?: boolean): void
  startMeasurement(): void
  stopMeasurement(): void
  getMeasurementState(): { value: number }
  getFaceState(): { value: number }
  getMeasurementProgressPercentage(): number
  getMeasurementResults(): ShenAiMeasurementResults | null
  readonly MeasurementPreset: Record<string, { value: number }>
  readonly CameraMode: Record<string, { value: number }>
  readonly OperatingMode: Record<string, { value: number }>
  readonly OnboardingMode: Record<string, { value: number }>
  readonly InitializationMode: Record<string, { value: number }>
}

let moduleLoad: Promise<(args: unknown) => Promise<VendorSdk>> | null = null

/**
 * Loads the vendor bundle, which is an ES module served as a static asset.
 *
 * A DYNAMIC IMPORT of a same-origin URL, for the reason browser-mindset-client.ts records: `import()`
 * is a script FETCH and is exactly what `script-src 'self'` admits, where the inline `<script
 * type="module">` this pattern replaced needed `'unsafe-inline'` and was refused by the deployed
 * page's policy. `@vite-ignore` keeps the bundler's hands off a specifier it would otherwise try to
 * resolve and bundle at build time - the vendor bundle is a static asset in public/, not a module in
 * this app's graph, and it is 33 MB.
 *
 * The `window.__shenaiCreateSdk` check is the seam a Playwright journey injects through, and the
 * warm path on a second scan in one page.
 */
function loadVendorFactory(): Promise<(args: unknown) => Promise<VendorSdk>> {
  if (window.__shenaiCreateSdk) {
    return Promise.resolve(window.__shenaiCreateSdk)
  }

  moduleLoad ??= import(/* @vite-ignore */ SDK_MODULE_URL).then((module) => {
    const factory = (module as { default?: (args: unknown) => Promise<VendorSdk> }).default

    if (!factory) {
      throw new Error(`${SDK_MODULE_URL} loaded without a default export`)
    }

    return factory
  })

  return moduleLoad
}

/**
 * The real adapter. Everything with a decision in it is in shenai-session.ts behind
 * `ShenAiVitalClient`; this file is the part that cannot be unit-tested because it needs a camera, a
 * WebGL2 canvas and a 33 MB WASM module.
 *
 * **The camera is this app's to open, not the vendor's** - the same rule both other vendors now
 * follow. Shen can open its own, but `getUserMedia` reached after 33 MB of loading is a silent
 * refusal on every browser on an iPhone (docs/decisions.md D-24), so the stream opened inside the
 * patient's tap is handed over with `setMediaStream`.
 */
export function createBrowserShenAiClient(options: ShenAiVitalClientOptions & {
  /** The canvas the vendor renders into. Required: its pipeline is WebGL2 on a canvas. */
  readonly canvasId: string
  /**
   * Whether the vendor draws its face mesh into the preview. Defaults to on, which is what the
   * single-vendor path wants (see sessionSettings).
   *
   * OFF for a hybrid scan, where this canvas sits UNDERNEATH an opaque video and nothing drawn on it
   * is ever seen by anybody (docs/decisions.md D-38). A mesh rendered for no viewer is GPU work and
   * GPU memory on the device that has neither to spare.
   */
  /**
   * The shape of the frames the camera is delivering, reported once, as width over height.
   *
   * READ OFF THE STREAM THIS CLIENT ALREADY HAS, and it changes nothing about when that stream is
   * opened or who opens it. The scan surface needs it to crop the vendor's own preview to its frame
   * (scanning-screen.tsx) and there is no `<video>` on this path to measure instead.
   */
  readonly onCameraAspect?: (widthOverHeight: number) => void
  readonly drawFaceMask?: boolean
  /**
   * Which session this client runs. Two shapes, because there are two callers.
   *
   * `oneMinuteAllMetrics` is the default and is what this integration has always run: the vendor's
   * own `ONE_MINUTE_ALL_METRICS` preset, every metric it produces, for the single-vendor scan whose
   * whole reading comes from this vendor.
   *
   * `thirtySecondsAllMetrics` is the hybrid scan's (docs/decisions.md D-40): the vendor's own named
   * thirty-second preset, half the measurement of the default. Every metric, because narrowing them
   * needs `CUSTOM` and `CUSTOM` does not measure - see the verdict in D-40.
   */
  readonly measurement?: 'oneMinuteAllMetrics' | 'thirtySecondsAllMetrics'
}): ShenAiVitalClient {
  let sdk: VendorSdk | null = null
  /**
   * The same object as `sdk`, kept so `releaseRuntime()` still has something to call after
   * `destroy()` has nulled `sdk`. Two names for one runtime because they have two lifetimes: the
   * pipeline is torn down when the scan ends, and the heap is given back only when a caller says so.
   */
  let runtime: VendorSdk | null = null
  let stream: MediaStream | null = options.stream ?? null

  return {
    setMediaStream(next) {
      stream = next
      sdk?.setMediaStream(next, true)
    },

    async load(onProgress) {
      const factory = await loadVendorFactory()

      // THE THREE ARGUMENTS THAT ARE NOT DEFAULTS, and the reason each is set.
      //
      // `enableErrorReporting: false` is the important one. The vendor's index.mjs calls initSentry()
      // unless this is exactly false, and util/index.mjs carries a complete Sentry 8.55.0 client whose
      // DSN is not a string in the file - it is rebuilt at runtime from an XOR'd byte array and points
      // at the vendor's own Sentry project. This page holds a camera stream of a patient's face, and
      // its exceptions are not the vendor's to collect. See public/sdk/shenai/PROVENANCE.md, which
      // carries the decoded address, and note that check-vendor-surface.sh's third-party-origin grep
      // CANNOT see an address assembled from arithmetic - which is why this flag is asserted at the
      // call site instead.
      //
      // `enablePreloadDisplay: false` because the default draws the vendor's own loading screen, with
      // its logo, onto a canvas it goes looking for by the id "mxcanvas". This app has its own phase
      // text and its own progress bar (D-26), and a second loading screen underneath them is not a
      // design anyone chose.
      //
      // `locateFile` because the Emscripten runtime resolves shenai_sdk.wasm relative to the document
      // otherwise, and the document is never /sdk/shenai - it is the scan page, which since
      // docs/decisions.md D-41 is a bare credential at the root (`/GB7GMEGK`) and before that was
      // /v2/scan. The 33 MB fetch would 404 into an instantiation failure that says nothing about
      // paths. Every path this client names is absolute for that reason, so the shape of the document's
      // own URL changes nothing here - which is what made D-41 a change this file did not have to make.
      //
      // `onWasmLoadingProgress` is what makes the patient's bar move through a 33 MB download, and
      // THE VENDOR'S OWN TYPES NAME IT WRONG: `index.d.ts` declares `wasmLoadingProgressCallback`,
      // and `index.mjs` reads `config.onWasmLoadingProgress` - three times, and nothing reads the
      // other name. Typed correctly and it would have been a callback that never fired, which looks
      // exactly like a download that never progresses. The runtime wins over the declaration.
      sdk = runtime = await factory({
        enableErrorReporting: false,
        enablePreloadDisplay: false,
        locateFile: (file: string) => `${SDK_DIRECTORY}/${file}`,
        onWasmLoadingProgress: (progress: number) => {
          // The vendor reports 0..1 for the download alone. The caller decides what to do with it;
          // this client does not know there is a bar.
          if (typeof progress === 'number' && Number.isFinite(progress)) {
            onProgress?.(progress)
          }
        },
      })
    },

    async initialize(token, userId) {
      if (!sdk) {
        throw new Error('initialize() was called before load()')
      }

      // ORDER, AND IT IS NOT INTERCHANGEABLE. The vendor initializes FIRST and the canvas is attached
      // after - "1. Create the SDK instance, 2. Initialize, 3. Attach to canvas" in its own
      // documentation. Attaching first is not an error and throws nothing: it binds a canvas to a
      // runtime that has no pipeline yet, and the result is a preview that stays black while the
      // camera light is on, which is exactly what a patient reported.
      const result = await new Promise<number>((resolve) => {
        sdk!.initialize(
          token,
          userId,
          sessionSettings(sdk!, {
            drawFaceMask: options.drawFaceMask ?? true,
            measurement: options.measurement ?? 'oneMinuteAllMetrics',
          }),
          (outcome) => resolve(outcome.value),
        )
      })

      if (result !== 0) {
        // Not attached and no camera handed over: an uninitialized runtime has nothing to render, and
        // a dark canvas under a live camera light is the worst way to say "the token was refused".
        return result
      }

      // A CSS SELECTOR, not a bare id. The vendor's parameter is named `canvas` and its own example
      // is `attachToCanvas("#custom-id")` - Emscripten resolves it through the same selector lookup
      // its event targets use. A bare `fh-canvas` matches no element, and the call neither throws nor
      // returns anything to say so; it simply renders nowhere.
      const selector = `#${options.canvasId}`
      if (!document.querySelector(selector)) {
        // Said out loud because the symptom is silence. The scanning screen renders this canvas only
        // on the ShenAi branch, so a vendor mismatch here is a black rectangle and no error at all.
        console.warn('[shenai] no canvas matches the selector, so the preview will not render', {
          selector,
        })
      }
      sdk.attachToCanvas(selector)

      // The camera LAST: setMediaStream hands the stream to a runtime that is initialized and has a
      // surface to draw on. Handed over before initialize it is given to a pipeline that does not
      // exist yet.
      await attachCamera()

      return result
    },

    startMeasurement() {
      if (!sdk) return

      // THE MODE IS WHAT MEASURES, AND `startMeasurement()` ALONE DOES NOTHING.
      //
      // Shen has three operating modes and initializes in POSITIONING, which finds and frames a face
      // and nothing else. In that mode `startMeasurement()` is a no-op that returns nothing and
      // throws nothing: the state stays NOT_STARTED, `getMeasurementProgressPercentage()` stays at
      // zero, and after the measurement window closes `getMeasurementResults()` is null. On a real
      // iPhone that was a patient holding still for ninety seconds, watching a preview of their own
      // face and a progress bar that never moved, while the vendor reported `face: OK` throughout and
      // measured nothing (docs/decisions.md D-36).
      //
      // Switching to MEASURE is what starts the measurement. `startMeasurement()` is still called
      // after it, because the vendor documents it as the way to begin a session and the mode alone is
      // not documented to imply it - and a second call in the right mode is harmless.
      sdk.setOperatingMode(sdk.OperatingMode.MEASURE)
      sdk.startMeasurement()
    },

    stopMeasurement() {
      if (!sdk) return
      sdk.stopMeasurement()
      // Back to the mode the SDK initializes in, so a stopped session leaves the runtime where it
      // started rather than in a measuring state nothing is reading.
      sdk.setOperatingMode(sdk.OperatingMode.POSITIONING)
    },

    measurementState() {
      return sdk?.getMeasurementState().value ?? -1
    },

    faceState() {
      return sdk?.getFaceState().value ?? -1
    },

    progressPercentage() {
      return sdk?.getMeasurementProgressPercentage() ?? 0
    },

    results() {
      return sdk?.getMeasurementResults() ?? null
    },

    destroy() {
      stream?.getTracks().forEach((track) => track.stop())
      stream = null
      try {
        sdk?.deinitialize()

        // `destroyRuntime()` is NOT called, and the omission is deliberate. It tears the Emscripten
        // runtime down under a pipeline that deinitialize has only just stopped, and on iOS that
        // surfaced as `Aborted()` followed by "Unreachable code should not be executed" - a WASM
        // trap, in the console of a patient whose scan had already finished. Nothing needs it: the
        // page is either navigating to the completion screen or being closed, and the whole runtime
        // goes with the document either way. A tidy-up that traps is worse than no tidy-up
        // (docs/decisions.md D-35).
      } finally {
        sdk = null
      }
    },

    releaseRuntime() {
      // `destroyRuntime()` is what hands the 33 MB heap back, and it is what D-35 refused to call
      // because it trapped on iOS. A sequential scan has to have the memory back before the other
      // vendor loads, so it is called here - guarded, and by a runner that has already submitted the
      // reading (shenai-types.ts says why at length).
      //
      // `sdk` is normally already null because destroy() ran first, so the reference is captured
      // before that happens; the runner calls destroy() then this, in that order.
      try {
        runtime?.destroyRuntime?.()
      } catch (error) {
        // A trap here is the failure D-35 recorded. It is reported and swallowed: the measurement is
        // in hand and submitted, and the worst case is that the next vendor loads into a heap this
        // could not reclaim - which is the situation before this method existed.
        console.warn('[shenai] the runtime refused to be released', error)
      } finally {
        runtime = null
      }
    },
  }

  async function attachCamera(): Promise<void> {
    if (__FH_CAMERA_REPLACEMENT__ && window.__VIDEO_URL) {
      // The journey affordance, compiled out of the production bundle by the same define both other
      // vendor clients read. Shen takes a MediaStream, and a <video> playing recorded footage can be
      // captured into one - so the journey drives the real SDK against a scan whose answer is known.
      const video = document.createElement('video')
      video.muted = true
      video.setAttribute('playsinline', '')
      video.src = window.__VIDEO_URL
      video.loop = true
      await video.play()
      sdk?.setMediaStream((video as HTMLVideoElement & { captureStream(): MediaStream }).captureStream(), true)
      return
    }

    if (!stream) {
      // No pre-opened stream: the caller is not the scan flow. Kept as a fallback rather than a throw
      // so this client stays usable on its own, but the flow always hands one over - on iOS a
      // getUserMedia reached from here is a silent refusal (docs/decisions.md D-24).
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('This browser exposes no camera API')
      }
      stream = await navigator.mediaDevices.getUserMedia(PATIENT_CAMERA)
    }

    reportCameraAspect(stream)
    sdk?.setMediaStream(stream, true)
  }

  /**
   * Inert by construction: it reads what the track already reports and tells the caller. A camera
   * that will not describe itself is not a reason to fail a scan, so everything here is guarded and
   * the preview simply goes uncropped.
   */
  function reportCameraAspect(camera: MediaStream): void {
    try {
      const settings = camera.getVideoTracks()[0]?.getSettings()
      if (settings?.width && settings?.height) {
        options.onCameraAspect?.(settings.width / settings.height)
      }
    } catch (error) {
      console.warn('[shenai] the camera would not report its dimensions', error)
    }
  }
}

/**
 * The measurement preset, which on this installation is `ONE_MINUTE_ALL_METRICS` and nothing else
 * (docs/decisions.md D-40).
 *
 * **Two other presets have been tried on a real phone and neither measured.** `CUSTOM` with a metric
 * list, and then the named `THIRTY_SECONDS_ALL_METRICS`, both produced the same thing: the vendor
 * tracking a face perfectly happily - `face: OK` from two seconds in - while
 * `getMeasurementState()` sat at `NOT_STARTED` for the entire window and `getMeasurementResults()`
 * answered null. `setOperatingMode(MEASURE)` and `startMeasurement()` were called on both, exactly as
 * they are on the preset that works.
 *
 * So the caller's choice is READ, LOGGED, AND THEN OVERRULED, rather than the parameter being deleted:
 * the request is a real one - a shorter measurement is worth having - and the reason it cannot be
 * honoured belongs next to the request rather than in a commit message. When somebody learns which
 * entitlement or which SDK version makes the other presets work, this function is the one place that
 * changes.
 *
 * **A preset that resolves to nothing numeric is refused rather than sent.** The vendor's enum members
 * are boxed values (`{value: number}`), the SDK's own `.d.ts` has already promised three things its
 * runtime does not do, and an unrecognised member would arrive here as `undefined` and be handed to a
 * WASM binding that fails by doing nothing - which is precisely the ninety seconds of nothing D-36 was
 * written about. So the value is asserted before it is used, and a bad one is a loud line in the
 * console rather than a scan that measures a patient's face for a minute and produces null.
 */
function theOnlyPresetThatMeasures(
  sdk: VendorSdk,
  asked: 'oneMinuteAllMetrics' | 'thirtySecondsAllMetrics',
): { value: number } {
  const preset = sdk.MeasurementPreset.ONE_MINUTE_ALL_METRICS

  if (asked !== 'oneMinuteAllMetrics') {
    console.log('[shenai] a shorter measurement was asked for and is not available on this build', {
      asked,
      running: 'ONE_MINUTE_ALL_METRICS',
      why: 'the thirty-second and CUSTOM presets never leave NOT_STARTED here (docs/decisions.md D-40)',
    })
  }

  if (typeof preset?.value !== 'number') {
    // Nothing this app can do about it, and everything it can say about it. A preset the runtime does
    // not recognise is a measurement that never starts, and silence is how that cost ninety seconds
    // of a patient's time twice.
    console.error('[shenai] the vendor does not recognise ONE_MINUTE_ALL_METRICS, so this scan cannot measure', {
      resolved: preset,
      available: Object.keys(sdk.MeasurementPreset ?? {}),
    })
  }

  return preset
}

/**
 * EXPORTED for one reason: so the SHAPE of what is handed to the vendor is pinned by a test rather
 * than discovered by a patient (docs/decisions.md D-40). Everything else in this file needs a camera,
 * a WebGL2 canvas and 33 MB of WASM and is untestable by necessity; this is a pure function that
 * builds an object, and the object is a contract whose declarations have now lied three times.
 *
 * Every switch that turns the vendor's own interface OFF.
 *
 * **This app draws the scan, and it is not a preference.** The screen a patient sees here is one
 * this repository designed and localises - the face guide over the preview, the phase sentence, the
 * progress bar, and every signal the vendor raises shown together in red (D-26, D-28) - and
 * architecture §9 is that a patient reads a sentence this app wrote. Shen ships a complete
 * alternative: onboarding, a face overlay, visual warnings, a signal tile, a results summary and a
 * health-risks dashboard, in its own English and its own colours. Half of that on top of half of
 * this is not a design; it is two products in one rectangle.
 *
 * So the vendor is used as a measurement engine and nothing else, and the flags below are that
 * decision written out. They are set at initialize rather than toggled afterwards because a screen
 * that appears for one frame and is then switched off is worse than one that never appears.
 */
export function sessionSettings(
  sdk: VendorSdk,
  session: {
    readonly drawFaceMask: boolean
    readonly measurement: 'oneMinuteAllMetrics' | 'thirtySecondsAllMetrics'
  },
): Record<string, unknown> {
  const { drawFaceMask } = session

  return {
    // BP is the vital this whole flow exists to collect, and ONLY THE NAMED ALL_METRICS PRESETS
    // produce it. Which of them is the caller's: a hybrid scan takes the thirty-second one so the two
    // vendors' halves fit inside the patient's hold window (docs/decisions.md D-40, D-42).
    //
    // **And it is ALWAYS the one-minute preset, whatever the caller asks for** - see
    // theOnlyPresetThatMeasures. Two other presets have now been tried on a real phone and neither
    // measured anything at all (docs/decisions.md D-40).
    measurementPreset: theOnlyPresetThatMeasures(sdk, session.measurement),
    initializationMode: sdk.InitializationMode.MEASUREMENT,
    onboardingMode: sdk.OnboardingMode.HIDDEN,

    // Explicit, and it is also the vendor's default: the SDK comes up finding and framing a face
    // while the patient gets themselves into position, and the runner moves it to MEASURE when the
    // scan actually begins (see startMeasurement above). Named here so the two halves of that
    // transition are readable together rather than one being an unstated default.
    operatingMode: sdk.OperatingMode.POSITIONING,

    // THE CAMERA IS THIS APP'S, AND THIS IS THE FLAG THAT SAYS SO. Left at its default the vendor
    // opens its own with getUserMedia - after 33 MB has loaded, which on every browser on an iPhone
    // is a silent refusal because the user activation from the patient's tap is long gone. That is
    // D-24, in full, and it cost a release the last time. MEDIA_STREAM makes the SDK wait for the
    // stream this app opened in the tap and hands over with setMediaStream.
    cameraMode: sdk.CameraMode.MEDIA_STREAM,

    showUserInterface: false,
    showFacePositioningOverlay: false,
    showVisualWarnings: false,
    // ON, and the one piece of vendor drawing this app asks for. It is rendered INTO the preview -
    // a mesh over the face the vendor is tracking - so unlike its onboarding, warnings, signal tile
    // and results screens it does not compete with anything this app draws or say anything in the
    // vendor's own English. It is also the only visible confirmation that the vendor is tracking the
    // face it says it is, which on a scan that measured nothing for ninety seconds (D-36) is worth
    // having in front of the patient rather than only in a log.
    //
    // UNVERIFIED AGAINST `showUserInterface: false`: the vendor may gate all of its drawing behind
    // that flag, in which case this does nothing and the honest answer is that the mask cannot be had
    // without taking the rest of its interface too.
    //
    // A HYBRID SCAN PASSES FALSE, because there nobody can see this canvas at all: the Mindset video
    // is stacked on top of it, opaque, and the mesh is drawn for no viewer (docs/decisions.md D-38).
    showFaceMask: drawFaceMask,
    showBloodFlow: false,
    showSignalTile: false,
    showSignalQualityIndicator: false,
    showStartStopButton: false,
    showInfoButton: false,
    showTrialMetricLabels: false,
    showOutOfRangeResultIndicators: false,
    enableCameraSwap: false,
    enableStartAfterSuccess: false,
    enableSummaryScreen: false,
    showResultsFinishButton: false,
    enableHealthRisks: false,
    showHealthIndicesFinishButton: false,
    enableMeasurementsDashboard: false,
    hideShenaiLogo: true,

    // Nothing about this patient is left on this device. The scan is a one-shot measurement reached
    // from a link, there is no account to remember, and the assessment's own record is the platform's
    // (architecture §5). `saveHealthRisksFactors` follows for the same reason.
    localMemoryEnabled: false,
    saveHealthRisksFactors: false,

    /**
     * The face and nothing but the face.
     *
     * OFF EXPLICITLY, and the vendor's default is not visible from here: the flag is declared in
     * `index.d.ts` and consumed inside the WASM, so `index.mjs` never mentions it and there is nothing
     * in the bundle to read a default off. Named here because the direction is safe either way - a
     * flag already false costs nothing to set false, and one that was true is a whole frame processed
     * where an rPPG signal comes from a face region.
     *
     * It matters on a hybrid scan, where a second engine is processing the same frames at the same
     * time on a device with a hard memory ceiling (docs/decisions.md D-38).
     */
    enableFullFrameProcessing: false,
  }
}


